import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { Photo, UserRole, ViewMode } from '../types';

interface AppContextType {
  photos: Photo[];
  setPhotos: React.Dispatch<React.SetStateAction<Photo[]>>;
  addPhoto: (photo: Photo) => void;
  userRole: UserRole;
  setUserRole: (role: UserRole) => void;
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
  isWeddingDay: boolean;
  adminBypass: boolean;
  setAdminBypass: (bypass: boolean) => void;
  selectedPhotoId: string | null;
  setSelectedPhotoId: (id: string | null) => void;
  isUploadModalOpen: boolean;
  setIsUploadModalOpen: (open: boolean) => void;
  accessCode: string;
  setAccessCode: (code: string) => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

// October 3rd, 2026 at 2:30 PM UK time (BST = UTC+1)
// 14:30 BST = 13:30 UTC
const WEDDING_DATE = new Date('2026-10-03T13:30:00Z');

export function AppProvider({ children }: { children: React.ReactNode }) {
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [userRole, setUserRole] = useState<UserRole>(() => {
    const stored = localStorage.getItem('wedding_user_role');
    return (stored as UserRole) || null;
  });
  const [adminBypass, setAdminBypass] = useState<boolean>(() => {
    return localStorage.getItem('wedding_admin_bypass') === 'true';
  });
  const [accessCode, setAccessCode] = useState<string>(() => {
    return localStorage.getItem('wedding_access_code') || '';
  });
  const [selectedPhotoId, setSelectedPhotoId] = useState<string | null>(null);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  
  // Check if we're past the wedding date
  const isWeddingDay = new Date() >= WEDDING_DATE;
  
  // Determine view mode based on date and admin bypass
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    if (isWeddingDay || adminBypass) {
      return 'collage';
    }
    return 'countdown';
  });

  // Persist user role
  useEffect(() => {
    if (userRole) {
      localStorage.setItem('wedding_user_role', userRole);
    } else {
      localStorage.removeItem('wedding_user_role');
    }
  }, [userRole]);

  // Persist admin bypass
  useEffect(() => {
    if (adminBypass) {
      localStorage.setItem('wedding_admin_bypass', 'true');
    } else {
      localStorage.removeItem('wedding_admin_bypass');
    }
    // Update view mode when bypass changes
    if (adminBypass || isWeddingDay) {
      setViewMode('collage');
    } else {
      setViewMode('countdown');
    }
  }, [adminBypass, isWeddingDay]);

  // Persist access code
  useEffect(() => {
    if (accessCode) {
      localStorage.setItem('wedding_access_code', accessCode);
    } else {
      localStorage.removeItem('wedding_access_code');
    }
  }, [accessCode]);

  const addPhoto = useCallback((photo: Photo) => {
    setPhotos(prev => {
      // Check if photo already exists
      if (prev.some(p => p.id === photo.id)) {
        return prev;
      }
      return [photo, ...prev];
    });
  }, []);

  return (
    <AppContext.Provider
      value={{
        photos,
        setPhotos,
        addPhoto,
        userRole,
        setUserRole,
        viewMode,
        setViewMode,
        isWeddingDay,
        adminBypass,
        setAdminBypass,
        selectedPhotoId,
        setSelectedPhotoId,
        isUploadModalOpen,
        setIsUploadModalOpen,
        accessCode,
        setAccessCode,
      }}
    >
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
}

